/**
 * FAQ Page
 *
 * Frequently Asked Questions with category tabs and accordion
 * Task: T4.3.2 - FAQ page
 */

import { useState, useCallback, useEffect } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  Tabs,
  Box,
  TextField,
  Collapsible,
  Divider,
  Spinner,
} from '@shopify/polaris';
import { SearchIcon } from '@shopify/polaris-icons';

interface FAQ {
  id: string;
  category: string;
  question: string;
  answer: string;
  displayOrder: number;
}

// Default FAQs (used when API is unavailable)
const defaultFAQs: FAQ[] = [
  // Orders & Customization
  {
    id: '1',
    category: 'Orders & Customization',
    question: 'What is the Minimum Order Quantity (MOQ) for corporate gifts?',
    answer: 'At GiftSangam, we cater to businesses of all sizes. Generally, our MOQ starts at 50 units for standard items. However, for premium or highly customized products (like leather bags or tech gadgets), we can accommodate smaller batches. Please contact our sales team for specific product requirements.',
    displayOrder: 1,
  },
  {
    id: '2',
    category: 'Orders & Customization',
    question: 'Can I customize products with my company logo?',
    answer: 'Absolutely. We specialize in Brand Integration. Depending on the material, we offer screen printing, laser engraving, embossing (for leather), and embroidery (for apparel). We ensure your logo looks crisp and professional, aligning perfectly with your brand guidelines.',
    displayOrder: 2,
  },
  {
    id: '3',
    category: 'Orders & Customization',
    question: "Do you offer design support if I don't have a ready artwork?",
    answer: 'Yes! Our in-house design team can help visualize your logo on the product before production begins. We provide a Digital Mock-up for your approval to ensure the placement and size meet your expectations.',
    displayOrder: 3,
  },
  {
    id: '4',
    category: 'Orders & Customization',
    question: 'Can you source a specific product that is not in your catalog?',
    answer: "Yes. We pride ourselves on being your Sourcing Partners. If you have a specific item in mind—whether it's a unique tech gadget or a niche eco-friendly product—just share the reference image or details. Our global sourcing network will find the best match for you.",
    displayOrder: 4,
  },
  // Samples & Quality
  {
    id: '5',
    category: 'Samples & Quality',
    question: 'Can I see a sample before placing a bulk order?',
    answer: 'We highly encourage it. We understand the importance of "touch and feel." You can order a Pre-production Sample (usually chargeable). If you proceed with the bulk order, the sample cost is often adjusted against the final invoice.',
    displayOrder: 1,
  },
  {
    id: '6',
    category: 'Samples & Quality',
    question: 'How do you ensure product quality?',
    answer: 'Quality is our core promise. Every batch undergoes a strict Quality Check (QC) before dispatch. From checking the stitching on apparel to testing the battery life of electronic gadgets, we ensure that what you receive matches the sample approved.',
    displayOrder: 2,
  },
  // Shipping & Delivery
  {
    id: '7',
    category: 'Shipping & Delivery',
    question: 'Do you ship across India?',
    answer: 'Yes, we have a robust logistics network covering Pan-India. Whether your office is in a metro city like Mumbai or a remote location, we ensure safe and timely delivery.',
    displayOrder: 1,
  },
  {
    id: '8',
    category: 'Shipping & Delivery',
    question: 'What is the standard delivery timeline?',
    answer: 'Ready Stock Items: 3–5 business days. Customized/Branded Items: 7–14 business days, depending on the complexity of the branding. For urgent requirements, please mention "Rush Order" in your query, and we will try our best to expedite the process.',
    displayOrder: 2,
  },
  {
    id: '9',
    category: 'Shipping & Delivery',
    question: "Do you offer individual packaging and direct shipping to employees' homes?",
    answer: 'Yes, we offer Kitting and Fulfillment Services. If you are sending onboarding kits or festive hampers to remote employees, we can package each unit individually and ship them directly to multiple addresses (additional shipping charges apply).',
    displayOrder: 3,
  },
  // Payments & Billing
  {
    id: '10',
    category: 'Payments & Billing',
    question: 'Do you provide GST invoices?',
    answer: 'Yes, GiftSangam is a fully compliant entity under Novadhara Ventures. All orders come with a GST invoice, allowing you to claim input tax credit where applicable.',
    displayOrder: 1,
  },
  {
    id: '11',
    category: 'Payments & Billing',
    question: 'What are your payment terms?',
    answer: 'Typically, we require a 50% advance payment to confirm the order and initiate customization. The remaining balance is due before dispatch.',
    displayOrder: 2,
  },
];

export function FAQPage() {
  const [faqs, setFaqs] = useState<FAQ[]>(defaultFAQs);
  const [loading, setLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  // Get unique categories
  const categories = Array.from(new Set(faqs.map((faq) => faq.category)));

  const tabs = categories.map((category) => ({
    id: category.toLowerCase().replace(/\s+/g, '-'),
    content: category,
    panelID: `${category.toLowerCase().replace(/\s+/g, '-')}-panel`,
  }));

  // Fetch FAQs from API (optional - falls back to defaults)
  useEffect(() => {
    const fetchFAQs = async () => {
      setLoading(true);
      try {
        const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';
        const response = await fetch(`${API_URL}/content/faqs`);
        if (response.ok) {
          const data = await response.json();
          if (data.data && data.data.length > 0) {
            setFaqs(data.data);
          }
        }
      } catch {
        // Use default FAQs if API fails
        console.log('Using default FAQs');
      } finally {
        setLoading(false);
      }
    };

    fetchFAQs();
  }, []);

  const handleTabChange = useCallback((index: number) => {
    setSelectedTab(index);
  }, []);

  const toggleExpanded = useCallback((id: string) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  // Filter FAQs based on search and selected category
  const filteredFAQs = faqs.filter((faq) => {
    const matchesCategory = categories[selectedTab] === faq.category;
    const matchesSearch = searchQuery === '' ||
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesCategory && matchesSearch;
  }).sort((a, b) => a.displayOrder - b.displayOrder);

  if (loading) {
    return (
      <Page title="Frequently Asked Questions">
        <Box padding="800">
          <BlockStack align="center">
            <Spinner size="large" />
            <Text as="p">Loading FAQs...</Text>
          </BlockStack>
        </Box>
      </Page>
    );
  }

  return (
    <Page title="Frequently Asked Questions">
      <Layout>

        {/* Search */}
        <Layout.Section>
          <Card>
            <Box padding="400">
              <TextField
                label="Search FAQs"
                labelHidden
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search for questions..."
                prefix={<SearchIcon />}
                clearButton
                onClearButtonClick={() => setSearchQuery('')}
                autoComplete="off"
              />
            </Box>
          </Card>
        </Layout.Section>

        {/* FAQ Tabs and Content */}
        <Layout.Section>
          <Card>
            <Tabs tabs={tabs} selected={selectedTab} onSelect={handleTabChange}>
              <Box padding="400">
                <BlockStack gap="200">
                  {filteredFAQs.length === 0 ? (
                    <Box padding="400">
                      <Text as="p" tone="subdued" alignment="center">
                        No FAQs found matching your search.
                      </Text>
                    </Box>
                  ) : (
                    filteredFAQs.map((faq, index) => (
                      <div key={faq.id}>
                        {index > 0 && <Divider />}
                        <Box paddingBlock="300">
                          <button
                            onClick={() => toggleExpanded(faq.id)}
                            style={{
                              background: 'none',
                              border: 'none',
                              padding: 0,
                              cursor: 'pointer',
                              width: '100%',
                              textAlign: 'left',
                            }}
                          >
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}>
                              <Text as="h3" variant="headingSm">
                                {faq.question}
                              </Text>
                              <Text as="span" variant="bodyMd">
                                {expandedItems.has(faq.id) ? '−' : '+'}
                              </Text>
                            </div>
                          </button>

                          <Collapsible
                            open={expandedItems.has(faq.id)}
                            id={`faq-${faq.id}`}
                            transition={{ duration: '150ms', timingFunction: 'ease' }}
                          >
                            <Box paddingBlockStart="300">
                              <Text as="p" variant="bodyMd" tone="subdued">
                                {faq.answer}
                              </Text>
                            </Box>
                          </Collapsible>
                        </Box>
                      </div>
                    ))
                  )}
                </BlockStack>
              </Box>
            </Tabs>
          </Card>
        </Layout.Section>

        {/* Contact CTA */}
        <Layout.Section>
          <Card>
            <Box padding="600" background="bg-surface-secondary">
              <BlockStack gap="300" align="center">
                <Text as="h2" variant="headingMd" alignment="center">
                  Still have questions?
                </Text>
                <Text as="p" variant="bodyMd" alignment="center" tone="subdued">
                  Can't find what you're looking for? Our team is here to help.
                </Text>
                <a href="/contact" style={{ textDecoration: 'none' }}>
                  <Text as="span" variant="bodyMd" fontWeight="semibold">
                    Contact Us →
                  </Text>
                </a>
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
